import { differenceInWeeks, isThisWeek } from 'core/utils/date'
import { cloneDeep } from 'core/utils/lang'

import { type PreferredLane } from './PreferredLane'

export type LaneHistory = PreferredLane & {
  suggestions: Array<Date>
  bids: Array<{
    date: Date
    loadId: string
  }>
  books: Array<{
    date: Date
  }>
  searchs: Array<{
    date: Date
  }>
  createdAt: Date
  updatedAt: Date
}

export function createLaneHistory(preferredLane: PreferredLane): LaneHistory {
  const date = new Date()

  return {
    ...cloneDeep(preferredLane),
    suggestions: [],
    bids: [],
    books: [],
    searchs: [],
    createdAt: date,
    updatedAt: date,
  }
}

export function addSuggestionOnLaneHistory(laneHistory: LaneHistory): LaneHistory {
  const result = cloneDeep(laneHistory)
  const date = new Date()

  result.updatedAt = date
  result.suggestions.push(date)

  return result
}

export function getLaneHistorySuggestionCount(laneHistory: LaneHistory): number {
  return laneHistory.suggestions.length
}

export function getLaneHistoryLastSuggestion(laneHistory: LaneHistory): Date | undefined {
  if (laneHistory.suggestions.length === 0) {
    return undefined
  }

  return laneHistory.suggestions[laneHistory.suggestions.length - 1]
}

export function addBidToLaneHistory(laneHistory: LaneHistory, loadId: string): LaneHistory {
  const result = cloneDeep(laneHistory)
  const date = new Date()
  let { bids } = result

  bids = bids.filter(bid => bid.loadId !== loadId)
  bids.push({ date, loadId })
  bids = bids.slice(-3)

  result.updatedAt = date
  result.bids = bids

  return result
}

export function addBookToLaneHistory(laneHistory: LaneHistory): LaneHistory {
  const result = cloneDeep(laneHistory)
  const date = new Date()
  let { books } = result

  books.push({ date })
  books = books.slice(-3)

  result.updatedAt = date
  result.books = books

  return result
}

export function addSearchToLaneHistory(laneHistory: LaneHistory): LaneHistory {
  const result = cloneDeep(laneHistory)
  const date = new Date()
  let { searchs } = result

  searchs.push({ date })
  searchs = searchs.slice(-3)

  result.updatedAt = date
  result.searchs = searchs

  return result
}

export function shouldSuggestOnBid(laneHistory: LaneHistory): boolean {
  const lastSuggestion = getLaneHistoryLastSuggestion(laneHistory)

  // Should not suggest if was suggested on this week
  if (lastSuggestion != null && isThisWeek(lastSuggestion)) {
    return false
  }

  // Should not suggest if was suggested 5 times or more
  if (getLaneHistorySuggestionCount(laneHistory) >= 5) {
    return false
  }

  // Should not suggest if there is less than 3 bids on this lane for different loads
  if (laneHistory.bids.length < 3) {
    return false
  }

  // Should not suggest if the last 3 bids were not made on the last 3 weeks
  if (differenceInWeeks(laneHistory.bids[0].date, new Date()) >= 3) {
    return false
  }

  return true
}

export function shouldSuggestOnBook(laneHistory: LaneHistory): boolean {
  const lastSuggestion = getLaneHistoryLastSuggestion(laneHistory)

  // Should not suggest if was suggested on this week
  if (lastSuggestion != null && isThisWeek(lastSuggestion)) {
    return false
  }

  // Should not suggest if was suggested 5 times or more
  if (getLaneHistorySuggestionCount(laneHistory) >= 5) {
    return false
  }

  return true
}

export function shouldSuggestOnSearch(laneHistory: LaneHistory): boolean {
  const lastSuggestion = getLaneHistoryLastSuggestion(laneHistory)

  // Should not suggest if was suggested on this week
  if (lastSuggestion != null && isThisWeek(lastSuggestion)) {
    return false
  }

  // Should not suggest if was suggested 5 times or more
  if (getLaneHistorySuggestionCount(laneHistory) >= 5) {
    return false
  }

  // Should not suggest if there is less than 3 searchs on this lane
  if (laneHistory.searchs.length < 3) {
    return false
  }

  // Should not suggest if the last 3 searchs were not made on the last 3 weeks
  if (differenceInWeeks(new Date(), laneHistory.searchs[0].date) >= 3) {
    return false
  }

  return true
}
